import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { Layout } from './app/Layout';
import { isTestEnvironment } from './shared/config/environment';
import { ROUTES, homePath } from './shared/config/routes';
import { Toaster } from 'sonner';
import { ConfirmHost } from './shared/ui/ConfirmHost';
import { DataProvider, AuthProvider } from './app/providers';
import { useAuthStore } from './shared/store/authStore';

import { LoginPage } from './pages/login/LoginPage';
import { PartnerPage } from './pages/partner/PartnerPage';
import { PartnerJoinPage } from './pages/partner/PartnerJoinPage';
import { OAuthCallbackPage } from './pages/oauth-callback';

// Статистика
import { StatsLayout } from './pages/stats/StatsLayout';
import { StatsSummaryPage } from './pages/stats/StatsSummaryPage';
import { StatsReportPage } from './pages/stats/StatsReportPage';
import { StatsHistoryScreen } from './pages/stats/StatsHistoryScreen';
import { GeneralStatisticsTab } from './features/statistics/components/GeneralStatisticsTab';
import { LinkStatisticsTab } from './features/statistics/components/LinkStatisticsTab';
import { AverageViewsTab } from './features/statistics/components/AverageViewsTab';
import { TimelineTab } from './features/statistics/components/TimelineTab';
import { PartnersTab } from './pages/stats/PartnersTab';
import { AccountAnalyticsPage } from './pages/analytics/AccountAnalyticsPage';

// Публикации
import { PublicationsLayout } from './pages/publications/PublicationsLayout';
import { PublicationsPage } from './pages/publications/PublicationsPage';
import { PublicationDetailPage } from './pages/publications/PublicationDetailPage';
import { PublicationNewPage } from './pages/publications/PublicationNewPage';
import { AllPublicationsPage } from './pages/all-publications/AllPublicationsPage';
import { AllPublicationDetailPage } from './pages/all-publications/AllPublicationDetailPage';
import { ScheduledSection } from './widgets/scheduled-publications';

// Аккаунты
import { AccountsLayout } from './pages/accounts/AccountsLayout';
import { AccountsPage } from './pages/accounts/AccountsPage';
import { AccountTemplatesTab } from './pages/accounts/AccountTemplatesTab';
import { TemplatePage } from './pages/templates/TemplatePage';
import { CommentModerationPage } from './pages/comment-moderation/CommentModerationPage';

// Конвейер контента. Прежние экраны («Мой день», «Задачи», «Проверка»,
// «Публикация», «Сегодня», «Неделя») больше не открываются — их адреса ведут
// в «Монтаж»; сами файлы страниц пока лежат на месте.
import { useVisibleNavSections } from './entities/pipeline';

// Сотрудники и доступы
import { TeamPage } from './pages/team/TeamPage';
import { DivisionPage } from './pages/division';
import { MontageModerationPage, MontagePage, MontageVideosPage, MyTasksPage, TakesPage, WorkPage } from './features/montage';

// Витрина компонентов — отдельным куском сборки: на бою маршрут не
// регистрируется вовсе, поэтому файл туда просто не запрашивается.
const UiKitPage = lazy(() =>
  import('./pages/ui-kit/UiKitPage').then((m) => ({ default: m.UiKitPage })),
);
import { UsersPage } from './pages/admin/UsersPage';
import { AssignmentsPage } from './pages/admin/AssignmentsPage';

/**
 * Куда вести с «/»: в последний открытый раздел, иначе в первый пункт меню
 * по роли (тот же порядок, что в сайдбаре). Для монтажёра «Статистика» —
 * закрытый раздел, и корень не должен упираться в него первым же переходом.
 */
function HomeRedirect() {
  const sections = useVisibleNavSections();
  // Пока роль не приехала, не уводим никуда: иначе монтажёр каждый раз
  // оказывался бы в «Статистике», которая ему закрыта.
  if (!sections) return null;
  return <Navigate to={homePath(sections)} replace />;
}

/** Прежние экраны конвейера: монтажёру — его задачи, остальным — доска «Монтажа». */
function LegacyPipelineRedirect() {
  const sections = useVisibleNavSections();
  if (!sections) return null;
  const editor = sections.some((s) => s.path === ROUTES.MY_TASKS) && !sections.some((s) => s.path === ROUTES.MONTAGE);
  return <Navigate to={editor ? ROUTES.MY_TASKS : ROUTES.MONTAGE} replace />;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const userId = useAuthStore((state) => state.userId);
  const isLoading = useAuthStore((state) => state.isLoading);
  const role = useAuthStore((state) => state.role);

  if (isLoading) return null;
  if (!userId) return <Navigate to={ROUTES.LOGIN} replace />;
  // Партнёру из всего сайта положена одна страница (23.10.2026).
  if (role === 'partner') return <Navigate to={ROUTES.PARTNER} replace />;

  return <>{children}</>;
}

/** Страница партнёра — только партнёру; сотрудника уводим на его главную. */
function PartnerRoute() {
  const userId = useAuthStore((state) => state.userId);
  const isLoading = useAuthStore((state) => state.isLoading);
  const role = useAuthStore((state) => state.role);

  if (isLoading) return null;
  if (!userId) return <Navigate to={ROUTES.LOGIN} replace />;
  if (role !== 'partner') return <Navigate to="/" replace />;
  return <PartnerPage />;
}

export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={<div style={{ padding: 24 }}>Что-то пошло не так. Обновите страницу.</div>}>
      <BrowserRouter>
        <AuthProvider>
          <DataProvider>
            <Routes>
              <Route path={ROUTES.LOGIN} element={<LoginPage />} />
              <Route path="/oauth/callback" element={<OAuthCallbackPage />} />
              <Route path={ROUTES.PARTNER_JOIN} element={<PartnerJoinPage />} />
              <Route path={ROUTES.PARTNER} element={<PartnerRoute />} />

              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<HomeRedirect />} />

                {/* ── Конвейер контента ──────────────────────────────────── */}
                <Route path={ROUTES.MODERATION} element={<MontageModerationPage />} />
                {/* Прежние экраны конвейера — в «Монтаж» */}
                <Route path={ROUTES.MY_DAY} element={<LegacyPipelineRedirect />} />
                <Route path={`${ROUTES.CONTENT_TASKS}/*`} element={<LegacyPipelineRedirect />} />
                <Route path={ROUTES.HEAD_TODAY} element={<Navigate to={ROUTES.MONTAGE_CALENDAR} replace />} />
                <Route path={ROUTES.HEAD_WEEK} element={<Navigate to={ROUTES.MONTAGE_CALENDAR} replace />} />
                <Route path={ROUTES.DIVISION} element={<DivisionPage />} />
                <Route path={ROUTES.MONTAGE_TOPICS} element={<Navigate to={ROUTES.MONTAGE} replace />} />
                <Route path={ROUTES.VIDEOS} element={<MontageVideosPage />} />
                <Route path={ROUTES.TAKES} element={<TakesPage />} />
                <Route path={ROUTES.WORK} element={<WorkPage />} />
                <Route path={ROUTES.MONTAGE} element={<MontagePage />} />
                <Route path={ROUTES.MONTAGE_CALENDAR} element={<MontagePage />} />
                <Route path={ROUTES.MY_TASKS} element={<MyTasksPage />} />
                <Route path={ROUTES.MY_TASKS_CALENDAR} element={<MyTasksPage />} />
                <Route path={ROUTES.MY_TASKS_POSTS} element={<MyTasksPage />} />

                {/* ── Статистика: пять срезов одного раздела ─────────────── */}
                <Route path={ROUTES.STATS} element={<StatsLayout />}>
                  <Route index element={<StatsSummaryPage />} />
                  <Route path="accounts" element={<GeneralStatisticsTab />} />
                  <Route path="links" element={<LinkStatisticsTab />} />
                  <Route path="averages" element={<AverageViewsTab />} />
                  <Route path="timeline" element={<TimelineTab />} />
                  <Route path="partners" element={<PartnersTab />} />
                </Route>
                {/* Подэкраны статистики — без вкладок, со ссылкой «назад» */}
                <Route path={ROUTES.STATS_REPORT} element={<StatsReportPage />} />
                <Route path="/stats/history" element={<StatsHistoryScreen />} />
                <Route path={ROUTES.ANALYTICS_ACCOUNT} element={<AccountAnalyticsPage />} />

                {/* ── Публикации ─────────────────────────────────────────── */}
                <Route path={ROUTES.PUBLICATIONS} element={<PublicationsLayout />}>
                  <Route index element={<PublicationsPage />} />
                  <Route path="scheduled" element={<ScheduledSection />} />
                  <Route path="feed" element={<AllPublicationsPage />} />
                  <Route path="comments" element={<CommentModerationPage />} />
                </Route>
                <Route path={ROUTES.PUBLICATION_NEW} element={<PublicationNewPage />} />
                <Route path={ROUTES.PUBLICATION_DETAIL} element={<PublicationDetailPage />} />
                <Route path={ROUTES.ALL_PUBLICATION_DETAIL} element={<AllPublicationDetailPage />} />

                {/* Разбор комментариев переехал во вкладку «Публикаций» */}
                <Route path={ROUTES.COMMENTS} element={<Navigate to={ROUTES.PUBLICATIONS_COMMENTS} replace />} />

                {/* ── Аккаунты ───────────────────────────────────────────── */}
                <Route path={ROUTES.ACCOUNTS} element={<AccountsLayout />}>
                  <Route index element={<AccountsPage />} />
                  <Route path="templates" element={<AccountTemplatesTab />} />
                </Route>
                <Route path={ROUTES.TEMPLATE_NEW} element={<TemplatePage />} />
                <Route path={ROUTES.TEMPLATE_EDIT} element={<TemplatePage />} />

                {/* Витрина компонентов существует только на тестовом стенде:
                    на бою маршрута нет, и «/ui-kit» уходит в общий редирект. */}
                {isTestEnvironment() && (
                  <Route
                    path={ROUTES.UI_KIT}
                    element={
                      <Suspense fallback={null}>
                        <UiKitPage />
                      </Suspense>
                    }
                  />
                )}

                {/* Профиль стал окном (открывается из сайдбара и таб-бара);
                    старую ссылку не роняем в 404 — ведём на главную по роли. */}
                <Route path={ROUTES.PROFILE} element={<Navigate to="/" replace />} />

                {/* ── Сотрудники и доступы ───────────────────────────────── */}
                <Route path={ROUTES.TEAM} element={<TeamPage />}>
                  <Route index element={<UsersPage />} />
                  <Route path="assignments" element={<AssignmentsPage />} />
                </Route>

                {/* ── Прежние адреса: сохраняем ссылки и закладки ─────────── */}
                <Route path={ROUTES.DASHBOARD} element={<Navigate to={ROUTES.STATS} replace />} />
                <Route path={ROUTES.ACCOUNTS_STATS} element={<Navigate to={ROUTES.STATS} replace />} />
                <Route path={ROUTES.STATS_HISTORY} element={<Navigate to="/stats/history" replace />} />
                <Route path={ROUTES.HARVEST} element={<Navigate to={ROUTES.STATS} replace />} />
                <Route path={ROUTES.ANALYTICS} element={<Navigate to={ROUTES.STATS_ACCOUNTS} replace />} />
                <Route path="/analytics/general" element={<Navigate to={ROUTES.STATS_ACCOUNTS} replace />} />
                <Route path="/analytics/account" element={<Navigate to={ROUTES.STATS_ACCOUNTS} replace />} />
                <Route path="/analytics/links" element={<Navigate to={ROUTES.STATS_LINKS} replace />} />
                <Route path="/analytics/averages" element={<Navigate to={ROUTES.STATS_AVERAGES} replace />} />
                <Route path="/analytics/timeline" element={<Navigate to={ROUTES.STATS_TIMELINE} replace />} />
                <Route path={ROUTES.ALL_PUBLICATIONS} element={<Navigate to={`${ROUTES.PUBLICATIONS}/feed`} replace />} />
                <Route path={ROUTES.REELS} element={<Navigate to={`${ROUTES.PUBLICATION_NEW}?format=reel`} replace />} />
                <Route path={ROUTES.POSTS_CREATE} element={<Navigate to={`${ROUTES.PUBLICATION_NEW}?format=post`} replace />} />
                <Route path={ROUTES.TEMPLATES} element={<Navigate to={`${ROUTES.ACCOUNTS}/templates`} replace />} />
                <Route path={`${ROUTES.ADMIN}/*`} element={<Navigate to={ROUTES.TEAM} replace />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster position="top-right" />
            <ConfirmHost />
          </DataProvider>
        </AuthProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
}
